import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { workspaceApi, taskApi, pipelineApi, projectApi } from '@/lib/api'
import { useToast } from '@/stores/toast-store'
import { getActiveModuleDefinitions } from '@/lib/module-registry'
import type { TaskPriority, ModuleId, ModuleDataMap } from '@/types/task'
import { insertImageAtCursor, uploadImageFile } from '@/lib/image-upload'

const PRIORITY_OPTIONS: { value: TaskPriority; label: string }[] = [
  { value: 'urgent', label: 'Mendesak' },
  { value: 'high', label: 'Tinggi' },
  { value: 'medium', label: 'Sedang' },
  { value: 'low', label: 'Rendah' },
]

export default function NewTaskPage() {
  const { slug, projectId } = useParams<{ slug: string; projectId: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [statusId, setStatusId] = useState('')
  const [priority, setPriority] = useState<TaskPriority>('medium')
  const [dueDate, setDueDate] = useState('')
  const [assignedTo, setAssignedTo] = useState('')
  const [assetUrl, setAssetUrl] = useState('')
  const [moduleData, setModuleData] = useState<ModuleDataMap>({})
  const [error, setError] = useState<string | null>(null)
  const [imgUploading, setImgUploading] = useState(false)
  const imgFileInputRef = useRef<HTMLInputElement>(null)

  const { data: statuses } = useQuery({
    queryKey: ['workspace', slug, 'pipeline'],
    queryFn: () => pipelineApi.getStatuses(slug!),
    enabled: !!slug,
    select: (data) => {
      if (!statusId) {
        const def = data.find((s) => s.is_default)
        if (def) setStatusId(def.id)
      }
      return data
    },
  })

  const { data: members } = useQuery({
    queryKey: ['workspace', slug, 'members'],
    queryFn: () => workspaceApi.getMembers(slug!),
    enabled: !!slug,
  })

  const { data: project } = useQuery({
    queryKey: ['workspace', slug, 'project', projectId],
    queryFn: () => projectApi.getProject(slug!, projectId!),
    enabled: !!slug && !!projectId,
  })

  const { data: modulesData } = useQuery({
    queryKey: ['workspace', slug, 'project', projectId, 'modules'],
    queryFn: () => projectApi.getProjectModules(slug!, projectId!),
    enabled: !!slug && !!projectId,
    staleTime: 60_000,
  })
  const activeModules: ModuleId[] = modulesData?.active_modules ?? []

  // Pre-populate non-vault module data dari project defaults
  useEffect(() => {
    if (modulesData?.module_data) {
      setModuleData((prev) => ({ ...modulesData.module_data!, ...prev }))
    }
  }, [modulesData])

  // Auto-resize description textarea
  const descriptionRef = useRef<HTMLTextAreaElement>(null)
  useEffect(() => {
    const el = descriptionRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`
  }, [description])

  const createMutation = useMutation({
    mutationFn: () =>
      taskApi.createTask(slug!, {
        project_id: projectId!,
        title: title.trim(),
        description: description.trim() || undefined,
        status_id: statusId || undefined,
        priority,
        due_date: dueDate || undefined,
        assigned_to: assignedTo || undefined,
        asset_url: assetUrl.trim() || undefined,
        module_data: activeModules.length > 0 ? moduleData : undefined,
      }),
    onSuccess: (created) => {
      void queryClient.invalidateQueries({ queryKey: ['workspace', slug, 'project', projectId, 'tasks'] })
      void queryClient.invalidateQueries({ queryKey: ['dashboard', 'today'] })
      toast({ title: 'Tugas berhasil dibuat' })
      navigate(`/workspaces/${slug}/projects/${projectId}/tasks/${created.id}`, { replace: true })
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Gagal membuat tugas.')
    },
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    if (!title.trim()) {
      setError('Judul tugas wajib diisi.')
      return
    }
    createMutation.mutate()
  }

  async function handleImageUpload(file: File) {
    setImgUploading(true)
    try {
      const url = await uploadImageFile(file)
      if (descriptionRef.current) insertImageAtCursor(descriptionRef.current, url, setDescription)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Gagal mengunggah gambar.')
    } finally {
      setImgUploading(false)
      if (imgFileInputRef.current) imgFileInputRef.current.value = ''
    }
  }

  return (
    <div className="space-y-5">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-1.5 text-sm text-muted-foreground overflow-hidden">
        <Link to={`/workspaces/${slug}/projects`} className="hover:text-foreground shrink-0">
          Projects
        </Link>
        <span className="shrink-0">/</span>
        {project ? (
          <Link
            to={`/workspaces/${slug}/projects/${projectId}`}
            className="truncate hover:text-foreground max-w-[160px]"
          >
            {project.name}
          </Link>
        ) : (
          <span className="h-4 w-20 animate-pulse rounded bg-secondary inline-block" />
        )}
        <span className="shrink-0">/</span>
        <span className="shrink-0 text-foreground font-medium">Buat Tugas</span>
      </nav>

      <h1 className="text-xl font-semibold tracking-tight">Buat Tugas</h1>

      <form onSubmit={handleSubmit} className="flex flex-col md:flex-row gap-6 items-start">
        {/* Left — main content */}
        <div className="flex-1 min-w-0 space-y-4">
          {/* Title */}
          <div className="space-y-1.5">
            <label htmlFor="title" className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Judul <span className="text-destructive">*</span>
            </label>
            <input
              id="title"
              type="text"
              required
              autoFocus
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Nama tugas"
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>

          {/* Description */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label htmlFor="description" className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Deskripsi
              </label>
              <button
                type="button"
                disabled={imgUploading}
                onClick={() => imgFileInputRef.current?.click()}
                title="Sisipkan gambar"
                className="flex items-center gap-1 rounded px-1.5 py-0.5 text-xs text-muted-foreground hover:bg-secondary hover:text-foreground disabled:opacity-40"
              >
                {imgUploading
                  ? <span className="h-3 w-3 animate-spin rounded-full border border-current border-t-transparent" />
                  : '🖼'}
                <span>Gambar</span>
              </button>
            </div>
            <input
              ref={imgFileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) void handleImageUpload(f) }}
            />
            <textarea
              id="description"
              ref={descriptionRef}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              onPaste={(e) => {
                const imgItem = Array.from(e.clipboardData?.items ?? []).find(it => it.type.startsWith('image/'))
                if (imgItem) { e.preventDefault(); const f = imgItem.getAsFile(); if (f) void handleImageUpload(f) }
              }}
              disabled={imgUploading}
              placeholder="Tambahkan deskripsi..."
              rows={3}
              className={`w-full resize-none overflow-hidden rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring${imgUploading ? ' opacity-50 cursor-wait' : ''}`}
            />
          </div>

          {/* Module section */}
          {activeModules.length > 0 && (
            <div className="space-y-4 border-t border-border pt-4">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Modul Project</p>
              {getActiveModuleDefinitions(activeModules).map((mod) => {
                const Comp = mod.component
                const isVault  = mod.id === 'vault'
                const isBranch = mod.id === 'branch'
                return (
                  <div key={mod.id} className="space-y-1.5">
                    <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                      {mod.icon} {mod.label}
                      {isVault && (
                        <span className="ml-1.5 text-xs font-normal normal-case">(referensi project)</span>
                      )}
                    </label>
                    <Comp
                      moduleId={mod.id}
                      value={isVault ? modulesData?.module_data?.vault : moduleData[mod.id]}
                      onChange={(val: ModuleDataMap[ModuleId]) =>
                        setModuleData((prev) => ({ ...prev, [mod.id]: val }))
                      }
                      readOnly={isVault}
                      {...(isBranch && { taskMode: true })}
                    />
                  </div>
                )
              })}
            </div>
          )}

        </div>

        {/* Right — metadata sidebar */}
        <div className="w-full md:w-64 shrink-0 rounded-xl border border-border bg-card p-4 space-y-4 md:sticky md:top-6">
          {/* Status */}
          <div className="space-y-1.5">
            <label htmlFor="status" className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Status
            </label>
            <select
              id="status"
              value={statusId}
              onChange={(e) => setStatusId(e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="">Tanpa Status</option>
              {statuses?.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>

          {/* Priority */}
          <div className="space-y-1.5">
            <label htmlFor="priority" className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Prioritas
            </label>
            <select
              id="priority"
              value={priority}
              onChange={(e) => setPriority(e.target.value as TaskPriority)}
              className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            >
              {PRIORITY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          {/* Due date */}
          <div className="space-y-1.5">
            <label htmlFor="due-date" className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Due Date
            </label>
            <input
              id="due-date"
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>

          {/* Assignee */}
          <div className="space-y-1.5">
            <label htmlFor="assignee" className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Assignee
            </label>
            <select
              id="assignee"
              value={assignedTo}
              onChange={(e) => setAssignedTo(e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="">Tidak Ada</option>
              {members?.map((m) => (
                <option key={m.user_id} value={m.user_id}>
                  {m.display_name ?? m.email ?? m.user_id}
                </option>
              ))}
            </select>
          </div>

          {/* Asset URL */}
          <div className="space-y-1.5">
            <label htmlFor="asset-url" className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Asset Link
            </label>
            <input
              id="asset-url"
              type="url"
              value={assetUrl}
              onChange={(e) => setAssetUrl(e.target.value)}
              placeholder="https://..."
              className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>

          {/* Error + Actions */}
          <div className="space-y-3 border-t border-border pt-4">
            {error && (
              <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>
            )}
            <div className="flex flex-col gap-2">
              <button
                type="submit"
                disabled={createMutation.isPending}
                className="w-full rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary-hover disabled:opacity-50"
              >
                {createMutation.isPending ? 'Menyimpan...' : 'Buat Tugas'}
              </button>
              <button
                type="button"
                onClick={() => navigate(`/workspaces/${slug}/projects/${projectId}`)}
                className="w-full rounded-md border border-border px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-secondary hover:text-foreground"
              >
                Batal
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  )
}
